/**
 * Section Stagger Reveal
 * Slides in the visible blocks of a section one by one, skipping layout containers (group, grid, spacer...).
 */
(() => {
  // The script tag is rendered once per section using it, so guard against multiple executions.
  if (window.sectionStaggerRevealLoaded) return;
  window.sectionStaggerRevealLoaded = true;

  const CONTAINER_SELECTOR = '.group-block, .group-block-content, .grid-block, .layout-panel-flex';
  const SKIP_SELECTOR = [
    '.spacer-block',
    '.group-block__link',
    '.grid-block__link',
    '.group-block__media-wrapper',
    '.grid-block__media-wrapper',
    '.group-block__linee',
    '.overlay',
    'script',
    'style',
    'link',
    'template',
  ].join(', ');
  // Carousels are revealed as a single item: their off-screen slides would never intersect.
  const CAROUSEL_SELECTOR = ':scope > :is(.carousel-viewport, swiper-container, .swiper)';

  const collectItems = (el, items) => {
    Array.from(el.children).forEach((child) => {
      if (child.matches(SKIP_SELECTOR)) return;
      if (child.matches(CONTAINER_SELECTOR) && !child.querySelector(CAROUSEL_SELECTOR)) {
        collectItems(child, items);
      } else {
        items.push(child);
      }
    });
    return items;
  };

  // Wraps every word in a fixed mask so each line can slide up inside its own box, keeping inline markup (links, bold...).
  const splitWords = (el) => {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      if (!node.textContent.trim()) return;
      const fragment = document.createDocumentFragment();
      node.textContent.split(/([ \t\n\r]+)/).forEach((part) => {
        if (!part) return;
        if (!part.trim()) {
          fragment.appendChild(document.createTextNode(part));
          return;
        }
        const mask = document.createElement('span');
        mask.className = 'stagger-word';
        const inner = document.createElement('span');
        inner.className = 'stagger-word__inner';
        inner.textContent = part;
        mask.appendChild(inner);
        fragment.appendChild(mask);
      });
      node.replaceWith(fragment);
    });
  };

  // Lines are measured at reveal time, when the layout is final.
  const setLineIndexes = (el) => {
    let line = -1;
    let lastTop = null;
    el.querySelectorAll('.stagger-word').forEach((word) => {
      const top = Math.round(word.getBoundingClientRect().top);
      if (lastTop === null || Math.abs(top - lastTop) > 2) {
        line += 1;
        lastTop = top;
      }
      word.firstChild.style.setProperty('--line-index', line);
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries
        .filter((entry) => entry.isIntersecting)
        .map((entry) => entry.target)
        .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))
        .forEach((item, index) => {
          item.style.setProperty('--animation-order', index);
          if (item.classList.contains('text-block')) setLineIndexes(item);
          item.classList.add('is-visible');
          observer.unobserve(item);
        });
    },
    { rootMargin: '0px 0px -10% 0px' }
  );

  const initStaggerReveal = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const items = [];
    document.querySelectorAll('[data-stagger-reveal]:not([data-stagger-reveal-init])').forEach((wrapper) => {
      wrapper.setAttribute('data-stagger-reveal-init', '');
      collectItems(wrapper, []).forEach((item) => {
        item.classList.add('stagger-reveal-item');
        if (item.classList.contains('text-block')) splitWords(item);
        items.push(item);
      });
    });

    // Items already in view on landing would animate while the page is still painting, so wait for load.
    const observeItems = () => items.forEach((item) => observer.observe(item));
    if (document.readyState === 'complete') {
      observeItems();
    } else {
      window.addEventListener('load', observeItems, { once: true });
    }
  };

  initStaggerReveal();

  if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', initStaggerReveal);
  }
})();
